<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import ContactSwitchTimes from '@/components/ContactSwitchTimes.vue';
import { usePlaylistSwitchingContacts } from '@/composables/api/usePlaylistSwitchingApi';

/**
 * Inspector side-peek content for the Playlists report: the per-contact
 * breakdown of switches to one custom list over the report's date range.
 * Self-contained — fetches its own data from the props, like
 * {@link file:src/components/AudioFileIssueInspector.vue}.
 */
const props = defineProps<{
  locationZoneCustomPlaylistId: number;
  from: string;
  to: string;
  listLabel: string;
}>();

// Pull the full set the side-peek can show (the endpoint caps at 200); the
// header uses the range-wide totals from the response, not a sum of the page,
// so a list with more switchers than shown isn't silently undercounted.
const contactsQuery = usePlaylistSwitchingContacts(
  computed(() => props.locationZoneCustomPlaylistId),
  computed(() => ({ from: props.from, to: props.to, perPage: 200 })),
);

const rows = computed(() => contactsQuery.data.value?.data ?? []);
const totalSwitches = computed(() => contactsQuery.data.value?.total_switches ?? 0);
const totalContacts = computed(() => contactsQuery.data.value?.total_contacts ?? 0);
const isTruncated = computed(() => totalContacts.value > rows.value.length);

const numberFormatter = new Intl.NumberFormat();
function formatNumber(value: number | null | undefined): string {
  return numberFormatter.format(value ?? 0);
}

/** First + last initial for the avatar tile (falls back to "?" when unnamed). */
function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase();
}
</script>

<template>
  <div class="@container flex min-h-0 flex-col gap-4" data-testid="playlist-contact-breakdown">
    <div class="flex flex-col gap-0.5">
      <p class="text-sm font-medium tabular-nums">
        {{ formatNumber(totalSwitches) }} switch<span v-if="totalSwitches !== 1">es</span>
        across {{ formatNumber(totalContacts) }} contact<span v-if="totalContacts !== 1">s</span>
      </p>
      <p v-if="isTruncated" class="text-muted-foreground text-xs">
        Showing the top {{ formatNumber(rows.length) }}.
      </p>
    </div>

    <div
      v-if="contactsQuery.error.value"
      class="border-destructive/50 bg-destructive/10 text-destructive rounded-md border p-3 text-sm"
    >
      Could not load the contact breakdown.
    </div>

    <div class="grid grid-cols-1 gap-3">
      <Card
        v-for="contact in rows"
        :key="contact.contact_id"
        class="gap-0 overflow-hidden border-border/80 p-0 py-0 shadow-sm"
        data-testid="playlist-contact-row"
      >
        <Collapsible>
          <CollapsibleTrigger
            class="group flex w-full items-center gap-3 p-3 text-left outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
            :disabled="contact.switch_count === 0"
            data-testid="playlist-contact-toggle"
          >
            <div
              class="bg-muted text-muted-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase"
              aria-hidden="true"
            >
              {{ initials(contact.display_name) }}
            </div>
            <div class="flex min-w-0 flex-1 flex-col">
              <p
                class="truncate text-sm font-semibold leading-tight"
                :title="contact.display_name ?? undefined"
              >
                {{ contact.display_name ?? 'Unknown contact' }}
              </p>
              <p
                class="text-muted-foreground truncate text-xs leading-snug"
                :title="contact.email ?? undefined"
              >
                {{ contact.email ?? '—' }}
              </p>
            </div>
            <div class="flex shrink-0 flex-col items-end leading-none">
              <span class="text-base font-semibold tabular-nums">{{ formatNumber(contact.switch_count) }}</span>
              <span class="text-muted-foreground text-[10px] uppercase tracking-wide">
                switch<span v-if="contact.switch_count !== 1">es</span>
              </span>
            </div>
            <ChevronDownIcon
              v-if="contact.switch_count > 0"
              class="text-muted-foreground size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <!-- Mounts (and fetches the exact times) only when the card opens. -->
            <ContactSwitchTimes
              :location-zone-custom-playlist-id="locationZoneCustomPlaylistId"
              :contact-id="contact.contact_id"
              :from="from"
              :to="to"
            />
          </CollapsibleContent>
        </Collapsible>
      </Card>
      <p
        v-if="rows.length === 0 && !contactsQuery.isLoading.value"
        class="text-muted-foreground py-6 text-center text-sm"
      >
        No switches to this list in this range.
      </p>
    </div>
  </div>
</template>
